import { isTruckLoaded } from '@components/OrderWizard/util'
import { useModelConfig } from '@hooks/useModelConfig'
import { useEffect, useMemo, useState } from 'react'
import { useDestinationsContext } from '../../DestinationsContext'
import { useByProduct } from '@components/OrderWizard/Contexts/OrderContainersContext/ProductGroups/api'

type ProductVolumeStatus = 'below' | 'within' | 'above' | 'missing' | 'diff_to_loaded'

// TODO: Add full type interface in global Gravitate namespace
interface Tank {
  product: string
  tank_id: number
  manifold_id?: string
}

function instantiateProductGroup(tank: Tank, storeNumber: string, compartmentIndex: number) {
  return {
    product_name: tank.product,
    volume: undefined,
    targets: [
      {
        location_name: storeNumber,
        tank_id: tank.tank_id,
        manifold_id: tank.manifold_id,
        volume: 0,
      },
    ],
    store_number: storeNumber,
    compartment_index: compartmentIndex,
  }
}

export function useProductGroups(order: Gravitate.Order) {
  const { config } = useModelConfig()
  const { setOrderDestinations, getImpactedTankVolumes } = useDestinationsContext()
  const isPastLoading = useMemo(() => isTruckLoaded(order?.trip_status), [order])
  const allowShortLoadEntry = useMemo(() => config.allow_order_by_product_short_loads, [config])
  const [minimumVolume, setMinimumVolume] = useState(0)
  const [maximumVolume, setMaximumVolume] = useState(99_999)

  const [productGroups, setProductGroups] = useState<Record<string, Gravitate.SupplyOptionPayloadCompartment[]>>({}) // used in the new 'By Product' entry mode
  const [originalLoadedVolumes, setOriginalLoadedVolumes] = useState<Record<string, number>>({})
  const [originalProducts, setOriginalProducts] = useState<string[]>([])

  const { trimByProductMutation } = useByProduct()

  const resetProductGroups = () => {
    setProductGroups({})
    setOriginalLoadedVolumes({})
  }

  // serialization from product groups to traditional compartments array structure
  const compartments = useMemo(() => Object.values(productGroups ?? {}).flat(), [productGroups])

  // Recalculate impacted tank volume whenever volume allocations change in the product groupings
  useEffect(() => {
    const newDestinations = getImpactedTankVolumes(compartments)
    setOrderDestinations(newDestinations)
  }, [compartments])

  // Aggregation of volume total across all products
  const volumeTotal = useMemo(() => {
    if (!compartments || !compartments.length) return 0

    let sum = 0

    for (const c of compartments) {
      for (const target of c.targets) {
        sum += target.volume ?? 0
      }
    }

    return sum
  }, [compartments])

  useEffect(() => {
    if (!order?.compartments?.length || originalProducts.length) return
    const products = [...new Set(order?.compartments?.flatMap((c) => c.product_name))]
    setOriginalProducts(products)
  }, [order, originalProducts])

  useEffect(() => {
    // reduce product keys into object of same keys with volume totals from each target in the group
    if (Object.keys(originalLoadedVolumes ?? {}).length || !order) return

    const groups = getGroupsFromCompartments(order.compartments)

    const volumes = Object.entries(groups).reduce((acc, [k, v]) => {
      const aggregatedTotal = v.reduce((sum, c) => sum + (c.targets[0].volume ?? 0), 0)
      if (!acc[k]) {
        return { ...acc, [k]: aggregatedTotal }
      } else {
        return { ...acc, [k]: acc[k] + aggregatedTotal }
      }
    }, {}) as Record<string, number>

    setOriginalLoadedVolumes(volumes)
  }, [order])

  const mismatchedVolumes = useMemo(() => {
    if (!originalLoadedVolumes) return {}
    const volumeEntries = Object.entries(originalLoadedVolumes ?? {})

    return volumeEntries.reduce((acc, [product, originalVolume]) => {
      const matchingProducts = productGroups[product]
      if (!matchingProducts) {
        return { ...acc, [product]: originalVolume }
      }

      const newVolume = matchingProducts?.reduce((sum, c) => sum + c.targets?.reduce((sum, t) => sum + t.volume, 0), 0)

      if (newVolume !== originalVolume) {
        return { ...acc, [product]: originalVolume }
      }

      return acc
    }, {})
  }, [productGroups, originalLoadedVolumes])

  const volumeStatus: ProductVolumeStatus = useMemo(() => {
    if (order && isPastLoading && Object.keys(mismatchedVolumes).length) return 'diff_to_loaded'
    const targetVolumes = (compartments ?? []).flatMap((c) => c.targets.map((t) => t.volume))
    if (targetVolumes.some((tv) => !tv)) return 'missing'
    if (volumeTotal < minimumVolume && !allowShortLoadEntry) return 'below'
    if (volumeTotal > maximumVolume) return 'above'
    return 'within'
  }, [
    volumeTotal,
    compartments,
    allowShortLoadEntry,
    order,
    isPastLoading,
    minimumVolume,
    maximumVolume,
    mismatchedVolumes,
  ])

  // unique list of all stores this order is going to
  const destinations = useMemo(
    () => [
      ...new Set(compartments.flatMap((compartment) => compartment.targets.map((target) => target.location_name))),
    ],
    [compartments]
  )

  const isVolumeValid = useMemo(() => volumeStatus === 'within', [volumeStatus])

  async function addProductGroup(payload: { tank: Tank; store_number: string }) {
    const productEntries = Object.entries(productGroups)
    const compartmentIndex = compartments.length
    let compartment = instantiateProductGroup(payload.tank, payload.store_number, compartmentIndex)
    let newFirstCompartment: Gravitate.SupplyOptionPayloadCompartment
    if (!compartments?.length) {
      const results = await getInitialVolumesFirstSecondTargets(compartment)
      compartment = results[0]
    }

    if (compartments?.length === 1) {
      const results = await getInitialVolumesFirstSecondTargets(compartment)
      compartment = results[1]
      newFirstCompartment = results[0]
    }

    // existing product group
    if (productEntries.some(([product, _]) => product === compartment.product_name)) {
      setProductGroups((prevGroups) => {
        if (newFirstCompartment) {
          return {
            [compartment.product_name]: [newFirstCompartment, compartment],
          }
        }
        return {
          ...prevGroups,
          [compartment.product_name]: [...prevGroups[compartment.product_name], compartment],
        }
      })
      // new product group
    } else {
      setProductGroups((prevGroups) => {
        if (newFirstCompartment) {
          return {
            [newFirstCompartment.product_name]: [newFirstCompartment],
            [compartment.product_name]: [compartment],
          }
        }
        return {
          ...prevGroups,
          [compartment.product_name]: [compartment],
        }
      })
    }
  }

  async function getInitialVolumesFirstSecondTargets(compartment) {
    const first_store_number = compartment.store_number
    // when adding and removing compartments, sometimes the compartment_index is 1 for both compartments
    const fixedCompartments = [...compartments, compartment].map((item, index, array) => {
      const hasSameIndex = item.compartment_index === array[index + 1]?.compartment_index
      return {
        ...item,
        compartment_index: hasSameIndex ? item.compartment_index - 1 : item.compartment_index,
      }
    })
    const payload = {
      first_store_number,
      compartments: fixedCompartments,
    }
    return await trimByProductMutation.mutateAsync(payload)
  }

  function adjustAllocationVolume(productKey: string, allocationIndex: number, volume: number) {
    if (volume < 0) return
    const newProductGroups = structuredClone(productGroups)
    newProductGroups[productKey][allocationIndex].volume = volume // I don't think this top level volume is used for anything, but just in case.
    newProductGroups[productKey][allocationIndex].targets[0].volume = volume
    setProductGroups(newProductGroups)
  }

  function deleteAllocationsByStoreNumber(storeNumber: string) {
    setProductGroups((groups) => {
      const copiedGroups = structuredClone(groups)

      Object.entries(groups).forEach(([key, compartments]) => {
        const newCompartments = compartments.map((c) => ({
          ...c,
          targets: c.targets.filter((t) => t.location_name !== storeNumber),
        }))
        const recalcedCompartments = newCompartments.map((c) => ({
          ...c,
          volume: c.targets.reduce((acc, t) => acc + t.volume, 0),
        }))
        const prunedCompartments = recalcedCompartments.filter((c) => c.volume > 0)
        if (prunedCompartments.length > 0) {
          copiedGroups[key] = prunedCompartments
        } else {
          delete copiedGroups[key]
        }
      })

      return copiedGroups
    })

    // setProductGroups(prunedGroups)
  }

  function deleteProductGroupAllocation(productKey: string, allocationIndex: number) {
    const groupsCopy = structuredClone(productGroups)
    groupsCopy[productKey].splice(allocationIndex, 1)

    if (groupsCopy[productKey].length === 0) {
      delete groupsCopy[productKey]
    }

    setProductGroups(groupsCopy)
  }

  function hasExistingLocationTank(location: string, tankId: number) {
    return compartments.some((compartment) => {
      return compartment.targets.some((target) => {
        return target.location_name === location && target.tank_id === tankId
      })
    })
  }

  function getGroupsFromCompartments(compartments: Gravitate.SupplyOptionPayloadCompartment[]) {
    if (!compartments) return {}

    const mergedCompartmentsMap: { [key: string]: Gravitate.SupplyOptionPayloadCompartment } = {}

    // Iterate through compartments
    compartments.forEach((compartment) => {
      // Iterate through targets within each compartment
      compartment?.targets?.forEach((target) => {
        const key = `${target.location_name}_${compartment.product_name}_${compartment.store_number || ''}_${
          target.tank_id
        }`
        // Check if compartment with the same target location exists
        if (mergedCompartmentsMap[key]) {
          // Add volume to existing target location
          mergedCompartmentsMap[key].targets.find((t) => t.location_name === target.location_name).volume +=
            target.volume || 0
        } else {
          // Create new compartment with the target location
          const newCompartment: Gravitate.SupplyOptionPayloadCompartment = {
            compartment_index: compartment.compartment_index,
            product_short_name: compartment.product_short_name || null,
            product_name: compartment.product_name,
            targets: [{ ...target }],
            store_number: compartment.store_number || undefined,
          }
          mergedCompartmentsMap[key] = newCompartment
        }
      })
    })

    // Convert map values to array
    const mergedCompartments = Object.values(mergedCompartmentsMap)

    return mergedCompartments.reduce((acc, compartment) => {
      const key = compartment?.product_name
      if (!acc[key]) {
        return { ...acc, [key]: [compartment] }
      } else {
        return { ...acc, [key]: [...acc[key], compartment] }
      }
    }, {}) as Record<string, Gravitate.SupplyOptionPayloadCompartment[]>
  }

  function setProductGroupsFromCompartments(compartments: Gravitate.SupplyOptionPayloadCompartment[]) {
    setProductGroups(getGroupsFromCompartments(compartments))
  }

  function canAddProduct(tank: Gravitate.Tank) {
    if (hasExistingLocationTank(tank.store_number, tank.tank_id))
      return { canAdd: false, reason: 'A tank for this site has already been added' }
    if (isPastLoading && !originalProducts.includes(tank.product)) {
      return { canAdd: false, reason: `${tank.product} is not a part of initial load` }
    }
    if (compartments.length === 5) return { canAdd: false, reason: 'The maximum number of targets have been added (5)' } // Limit to 5 compartments similar to the pocket flow today
    return { canAdd: true, reason: '' }
  }

  return {
    productGroups,
    resetProductGroups,
    volumeTotal,
    isVolumeValid,
    addProductGroup,
    adjustAllocationVolume,
    deleteProductGroupAllocation,
    hasExistingLocationTank,
    instantiateProductGroup,
    volumeStatus,
    allowShortLoadEntry,
    minimumVolume,
    maximumVolume,
    setMinimumVolume,
    setMaximumVolume,
    compartments,
    destinations,
    getGroupsFromCompartments,
    setProductGroupsFromCompartments,
    deleteAllocationsByStoreNumber,
    canAddProduct,
    mismatchedVolumes,
    originalProducts,
    setOriginalLoadedVolumes,
  }
}

export type ProductGroupsApiType = ReturnType<typeof useProductGroups>
